// Angular
import { Injectable } from '@angular/core';
// Tranlsation
import { TranslateService } from '@ngx-translate/core';

export interface Locale {
	lang: string;
	// tslint:disable-next-line:ban-types
	data: Object;
}

@Injectable({
	providedIn: 'root'
})
export class TranslationService {
	// Private properties
	private langIds: any = [];

	/**
	 * Service Constructor
	 *
	 * @param translate: TranslateService
	 */
	constructor(private translate: TranslateService) {
		// add new langIds to the list
		this.translate.addLangs(['en','ar']);

		// this language will be used as a fallback when a translation isn't found in the current language
		this.translate.setDefaultLang('en');
	}

	/**
	 * Load Translation
	 *
	 * @param args: Locale[]
	 */
	loadTranslations(...args: Locale[]): void {
		const locales = [...args];

		locales.forEach(locale => {
			// use setTranslation() with the third argument set to true
			// to append translations instead of replacing them
			this.translate.setTranslation(locale.lang, locale.data, true);

			this.langIds.push(locale.lang);
		});

		// add new languages to the list
		this.translate.addLangs(this.langIds);
	}

	/**
	 * Setup language
	 *
	 * @param lang: any
	 */
	setLanguage(lang) {

		let langDefault = this.translate.getDefaultLang()
		if (!lang) {
			lang = langDefault;
		}
		this.translate.use(lang);
		localStorage.setItem('language', lang);
		this.changeLanguage(lang);
	}

	changeLanguage(lang: string) {
		const isRTL = lang === 'ar' || lang === 'ur';
		const rtlStylesheets = [
		  { href: 'assets/plugins/bootstrap/css/bootstrap-rtl.css', ltrHref: 'assets/plugins/bootstrap/css/bootstrap.css' },
		  { href: 'assets/css/style.angular.rtl.css', ltrHref: 'assets/css/style.angular.css' },
		];
	  
		const ltrStylesheets = [
		  { href: 'assets/plugins/bootstrap/css/bootstrap.css', rtlHref: 'assets/plugins/bootstrap/css/bootstrap-rtl.css' },
		  { href: 'assets/css/style.angular.css', rtlHref: 'assets/css/style.angular.rtl.css' },
		];
	  
		const stylesheets = isRTL ? rtlStylesheets : ltrStylesheets;
	  
		stylesheets.forEach(({ href, ltrHref, rtlHref }) => {
		  const existingLinkElement = document.querySelector(`link[rel="stylesheet"][href="${href}"]`);
		  if (!existingLinkElement) {
			const linkElement = document.createElement('link');
			linkElement.rel = 'stylesheet';
			linkElement.href = href;
			linkElement.onload = () => {
			  const oppositeLinkElement = document.querySelector(`link[href="${isRTL ? ltrHref : rtlHref}"]`);
			  if (oppositeLinkElement) {
				oppositeLinkElement.remove();
			  }
			};
			const viewportMetaTag = document.querySelector('meta[name="viewport"]');
			if (viewportMetaTag) {
				document.head.insertBefore(linkElement, viewportMetaTag.nextSibling);
			} else {
				document.head.insertBefore(linkElement, document.head.firstChild);
			}
		  } else {
			const oppositeLinkElement = document.querySelector(`link[href="${isRTL ? ltrHref : rtlHref}"]`);
			if (oppositeLinkElement) {
			  oppositeLinkElement.remove();
			}
		  }
		});
	  
		this.updateHtmlAttributes(lang, isRTL);
		this.updateBodyClasses(isRTL);
	  }
	  
	 updateHtmlAttributes(lang: string, isRTL: boolean) {
		const htmlElement = document.documentElement;
		const direction = isRTL ? 'rtl' : 'ltr';
		htmlElement.setAttribute('lang', lang);
		htmlElement.setAttribute('direction', direction);
		htmlElement.setAttribute('dir', direction);
		htmlElement.style.direction = direction;
	  }
	  
	  updateBodyClasses(isRTL: boolean) {
		const bodyElement = document.body;
		const classList = bodyElement.classList;
	  
		if (isRTL) {
		  classList.add('custom-rtl');
		  classList.remove('custom-ltr');
		} else {
		  classList.add('custom-ltr');
		  classList.remove('custom-rtl');
		}
	  }

	/**
	 * Returns selected language
	 */
	getSelectedLanguage(): any {
		return localStorage.getItem('language') || this.translate.getDefaultLang();
	}
}
